import { FC, ReactNode, useMemo, useState } from "react";
import { MDBBtn, MDBCol } from "mdb-react-ui-kit";
import { useRouter } from "next/router";
import CardTour from "@/components/home/cards/cardTour/CardTour";
import {
    sortByPriority,
    sortToursByPriorities,
} from "@/utils/common/sortByPriority";
import styles from "../../../../styles/navbar/NavBarM.module.scss";
import NavBarMLink from "./NavBarMLink";
import NavBarMBackIcon from "./NavBarMBackIcon";
import userInterfaceSvg from "../../../../utils/svg-icon/userInterface";
import { ToursByTourTypeEntity, ToursEntity } from "../../../../models/home";
import { ENavBarMenuMobile } from "../../../../models/common/enums";
import { TBreackpointsCarousel } from "../../../../models/common/common";
import Carousel from "../../carousel/Carousel";

// TODO: Remove eslint disable
type TagDestination = {
    _id: string;
    title: string;
    tagDestination: string;
    image?: { original?: string };
    tour?: ToursEntity[];
    priority?: number;
    tourPriorities?: Array<{ tourId: string; priority: number }>;
};

const NavBarMSubMenuSelected: FC<{
    className: string;
    icon: ReactNode;
    dropdownChoice: ENavBarMenuMobile;
    dropdownOptions: TagDestination[] | ToursByTourTypeEntity[];
    onClick: () => void;
}> = ({ className, icon, dropdownChoice, dropdownOptions, onClick }) => {
    const { ArrowRightSimpleLine } = userInterfaceSvg;
    const router = useRouter();
    const path = "/tours-destinations";

    const [selectedOption, setSelectedOption] = useState<number | null>(null);

    const options = useMemo(() => {
        return dropdownOptions.map(item => {
            if (dropdownChoice === ENavBarMenuMobile.ALL_TOURS) {
                return (item as ToursByTourTypeEntity).tourType;
            }
            return (item as TagDestination).title;
        });
    }, [dropdownOptions, dropdownChoice]);

    const breackPointCarousel: TBreackpointsCarousel = {
        0: {
            slidesPerView: 1.3,
            spaceBetween: 16,
            slidesOffsetAfter: 24,
            slidesOffsetBefore: 24,
        },
        576: {
            slidesPerView: 2.3,
            spaceBetween: 32,
            slidesOffsetAfter: 24,
            slidesOffsetBefore: 24,
        },
        768: {
            slidesPerView: 3,
            height: 149,
            spaceBetween: 32,
            slidesOffsetAfter: 24,
            slidesOffsetBefore: 24,
        },
        992: {
            slidesPerView: 1,
            width: 220,
            spaceBetween: 32,
            slidesOffsetAfter: 24,
            slidesOffsetBefore: 24,
        },
    };

    // Helper: ordina i tour per una destinazione
    const getSortedToursForDestination = (selected: TagDestination) => {
        if (selected?.tourPriorities && selected.tourPriorities.length > 0) {
            return sortToursByPriorities(
                selected.tourPriorities,
                selected?.tour ?? []
            );
        }
        return sortByPriority(
            tour => (tour.priority ? -tour.priority : undefined),
            selected?.tour ?? []
        );
    };

    // Helper: renderizza le card per i tour
    const renderTourCards = (tours: ToursEntity[]) => {
        return tours.map(element => (
            <CardTour
                className="mx-2 mx-md-0"
                key={element._id} // eslint-disable-line no-underscore-dangle
                content={element}
                isSmall
                height="50%"
                fromSection="NavBarMSubMenuSelected"
            />
        ));
    };

    const visibleCards = useMemo(() => {
        if (selectedOption === null) {
            return null;
        }

        if (dropdownChoice === ENavBarMenuMobile.ALL_TOURS) {
            const selected = dropdownOptions.at(
                selectedOption
            ) as ToursByTourTypeEntity;
            return renderTourCards(selected?.tours ?? []);
        }

        if (dropdownChoice === ENavBarMenuMobile.DESTINATIONS) {
            const selected = dropdownOptions.at(
                selectedOption
            ) as TagDestination;
            const sortedTours = getSortedToursForDestination(selected);
            return renderTourCards(sortedTours);
        }

        return null;
    }, [dropdownOptions, selectedOption, dropdownChoice]);

    const handleRouting = () => {
        const queryParams: string[] = [];
        if (
            dropdownChoice === ENavBarMenuMobile.ALL_TOURS &&
            selectedOption !== null
        ) {
            const tourType = (
                dropdownOptions.at(selectedOption) as ToursByTourTypeEntity
            )?.type;
            if (tourType) {
                queryParams.push(`tourType=${encodeURIComponent(tourType)}`);
            }
        }
        if (
            dropdownChoice === ENavBarMenuMobile.DESTINATIONS &&
            selectedOption !== null
        ) {
            const tagDestination = (
                dropdownOptions.at(selectedOption) as TagDestination
            )?.tagDestination;
            if (tagDestination) {
                // Trasforma "--" in "-and-" per usare "and" nell'URL invece di "&"
                const transformedTag = tagDestination.replace(/--/g, "-and-");
                queryParams.push(
                    `tagDestination=${encodeURIComponent(transformedTag)}`
                );
            }
        }
        const queryString =
            queryParams.length > 0 ? `?${queryParams.join("&")}` : "";
        const fullUrl = `${path}${queryString}`;

        if (router.pathname !== path) {
            router.push(fullUrl);
        } else {
            router.replace(fullUrl).then(() => router.reload());
        }
    };

    /* eslint-disable react/no-array-index-key */
    return (
        <MDBCol
            className={`${styles.navBarMSubMenu} ${className} container-fluid py-3 px-0 fadeInElement`}
        >
            <NavBarMLink
                icon={selectedOption != null ? "" : icon}
                icon2Active={<NavBarMBackIcon />}
                title={
                    selectedOption != null
                        ? dropdownOptions.at(selectedOption)?.title || ""
                        : ""
                }
                onClick={
                    selectedOption !== null
                        ? () => setSelectedOption(null)
                        : onClick
                }
                active
                subtitle=""
                icon2={undefined}
                className=""
            >
                {undefined}
            </NavBarMLink>
            <div
                className={`${styles.navBarMSubMenuSelectedContent} overflow-auto`}
            >
                {selectedOption === null &&
                    options.map((option, index) => (
                        <NavBarMLink
                            key={`${option}-${index}`}
                            title={option}
                            onClick={() => setSelectedOption(index)}
                            icon2={<ArrowRightSimpleLine />}
                            icon2Active={<NavBarMBackIcon />}
                            icon={undefined}
                            subtitle=""
                            active={false}
                            className=""
                        >
                            {undefined}
                        </NavBarMLink>
                    ))}
                {selectedOption !== null && (
                    <>
                        {dropdownChoice === ENavBarMenuMobile.ALL_TOURS && (
                            <div className="mb-2">
                                <p
                                    style={{
                                        fontFamily: "GeneralSans-Semibold",
                                        fontSize: "1rem",
                                        color: "#ed6f6f",
                                        margin: 0,
                                        paddingLeft: "2rem",
                                        marginLeft: "-0.5rem",
                                    }}
                                >
                                    {(() => {
                                        const tourType = (
                                            dropdownOptions.at(
                                                selectedOption
                                            ) as ToursByTourTypeEntity
                                        )?.type;
                                        switch (tourType) {
                                            case "city":
                                                return "3-5 days";
                                            case "region":
                                                return "3-9 days";
                                            case "italy":
                                                return "6-17 days";
                                            default:
                                                return "Scopri i nostri tour guidati";
                                        }
                                    })()}
                                </p>
                            </div>
                        )}
                        <MDBCol className="m-4 fadeInElement ">
                            <p
                                className={
                                    styles.navBarMSubMenuSelectedDescription
                                }
                            >
                                {dropdownChoice === ENavBarMenuMobile.ALL_TOURS
                                    ? (
                                          dropdownOptions.at(
                                              selectedOption
                                          ) as ToursByTourTypeEntity
                                      )?.body
                                    : ""}
                            </p>

                            <div className="d-flex justify-content-center my-3">
                                <MDBBtn
                                    className={`${styles.navBarMSubMenuSelectedBtn} rounded-9`}
                                    onClick={handleRouting}
                                >
                                    View More
                                </MDBBtn>
                            </div>
                        </MDBCol>
                    </>
                )}
                {visibleCards && (
                    <Carousel
                        classNameContainer="d-flex justify-content-center align-items-center mt-4 px-0 mx-0 w-100"
                        breakpoints={breackPointCarousel}
                        containerSizes={{ xs: 12, md: 12, size: 12 }}
                        visibleSlides={2}
                    >
                        {visibleCards.map(el => (
                            <div className="d-flex align-items-center container-fluid justify-content-center px-0 mx-0">
                                {el}
                            </div>
                        ))}
                    </Carousel>
                )}
            </div>
        </MDBCol>
    );
};
export default NavBarMSubMenuSelected;
